import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type UserSettings = Database['public']['Tables']['user_settings']['Row'];
type UserSettingsUpdate = Database['public']['Tables']['user_settings']['Update'];

const DEFAULT_SETTINGS: Omit<UserSettings, 'user_id' | 'created_at' | 'updated_at'> = {
  email_notifications: true,
  message_notifications: true,
  event_reminders: true,
  profile_visibility: 'members',
  notify_post_likes: true,
  notify_post_comments: true,
  notify_connection_requests: true,
  notify_mentions: true,
  notify_new_messages: true,
  notify_matchups: true,
  notify_comment_replies: true,
  notify_cohort_posts: true,
  notify_comment_likes: true,
};

export function useSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['settings', user?.id],
    queryFn: async (): Promise<UserSettings | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      // If no settings exist, return defaults
      if (!data) {
        return {
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          ...DEFAULT_SETTINGS,
        };
      }

      return data;
    },
    enabled: !!user?.id,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (updates: UserSettingsUpdate) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Try to update first
      const { data: existingData, error: selectError } = await supabase
        .from('user_settings')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existingData) {
        // Update existing
        const { data, error } = await supabase
          .from('user_settings')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            ...DEFAULT_SETTINGS,
            ...updates,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}
